import { GameController } from "../controller/GameController";
import { GameState, PlayerState } from "../../core/protocol/types";

type GameStateListener = (state: GameState | null) => void;

/**
 * MultiplayerRuntime owns the authoritative GameController instance plus
 * normalized game state snapshots that React (or any other host) can observe.
 * It mirrors the original C++ layering where the UI simply reacts to state
 * changes emitted from the runtime layer.
 */
export class MultiplayerRuntime {
  private readonly gameController: GameController;
  private state: GameState | null = null;
  private listeners: Set<GameStateListener> = new Set();

  constructor() {
    this.gameController = new GameController(this.handleStateUpdate);
  }

  /**
   * Return the underlying GameController. Runtime sessions use this to issue
   * network commands (host, join, lobby updates, etc).
   */
  getController(): GameController {
    return this.gameController;
  }

  /**
   * Subscribe to normalized GameState updates. The listener is invoked
   * immediately with the latest snapshot (if available).
   */
  subscribe(listener: GameStateListener): () => void {
    this.listeners.add(listener);
    if (this.state) {
      listener(this.state);
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get the latest cached GameState snapshot.
   */
  getState(): GameState | null {
    return this.state;
  }

  /**
   * Surface GameController's join rejection hook through the runtime.
   */
  setOnJoinRejected(callback: (reason: string) => void): void {
    this.gameController.setOnJoinRejected(callback);
  }

  /**
   * Surface GameController's error hook through the runtime.
   */
  setOnError(callback: (reason: string) => void): void {
    this.gameController.setOnError(callback);
  }

  /**
   * Dispose the runtime and cleanup controllers/listeners.
   */
  dispose(): void {
    this.listeners.clear();
    this.state = null;
    this.gameController.destroy();
  }

  private handleStateUpdate = (state: GameState): void => {
    const normalized = this.normalizeState(state);
    this.state = normalized;
    this.listeners.forEach((listener) => listener(normalized));
  };

  /**
   * Clone map entries so React hooks can rely on referential equality. This
   * mirrors the old GameControllerProvider behavior.
   */
  private normalizeState(state: GameState): GameState {
    const normalizedPlayers = new Map<number, PlayerState>();
    state.players.forEach((player, numPlayer) => {
      normalizedPlayers.set(numPlayer, { ...player });
    });

    const normalizedConnections = new Map<string, PlayerState>();
    normalizedPlayers.forEach((player) => {
      if (player.connectionId) {
        normalizedConnections.set(player.connectionId, player);
      }
    });

    return {
      ...state,
      players: normalizedPlayers,
      connections: normalizedConnections,
    };
  }
}


